// src/pages/CategoryPage.tsx
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getBlogPostsByCategory } from '../lib/blog';
import { categoryMap } from '../lib/categories'; // ✅ imported from categories.ts
import type { BlogPost } from '../types';
import { BlogCard } from '../components/BlogCard';
import { BlogSidebar } from '../components/BlogSidebar';
import { GlassCTA } from '../components/GlassCTA';

export function CategoryPage() {
  const { slug } = useParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    if (slug) {
      getBlogPostsByCategory(slug).then(setPosts);
    }
  }, [slug]);

  const injectedPosts = posts;

  // Use categoryMap to get the full name; fallback to capitalized slug
  const categoryTitle = slug && categoryMap[slug]
    ? categoryMap[slug]
    : slug
      ? slug.charAt(0).toUpperCase() + slug.slice(1)
      : '';

  return (
    <div className="min-h-screen bg-gray-950 text-yellow-100">
      <Helmet>
        <title>{`${categoryTitle} | Prop Viper Blog`}</title>
        <meta
          name="description"
          content={`Explore insightful blog articles in the ${categoryTitle} category. Learn, improve, and grow as a prop trader.`}
        />
      </Helmet>

      <section className="bg-gradient-to-br from-black to-gray-900 py-16 sm:py-20 text-center px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 text-yellow-400">
            {categoryTitle}
          </h1>
          <p className="text-base sm:text-lg text-yellow-200 mb-6">
            Dive into hand-picked articles related to {categoryTitle.toLowerCase()}. From expert tips to platform breakdowns — we cover it all.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 flex flex-col lg:flex-row gap-10">
        <div className="lg:w-3/4">
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            {injectedPosts.map((post, index) => (
              post === 'cta' ? (
                <div key={`cta-${index}`} className="h-full">
                  <GlassCTA variant="primary" position="top" promoUsedElsewhere={false} />
                </div>
              ) : (
                <BlogCard key={post.slug} post={post} />
              )
            ))}
          </div>
        </div>

        <aside className="lg:w-1/4 w-full">
          <BlogSidebar />
        </aside>
      </div>
    </div>
  );
}
